import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Button, Input } from "@/shared/ui";
import { LOGIN_PAGE_ROUTE } from "@/shared/utils";
import { AuthLayout } from "../components";
import { useForgotPasswordMutation } from "../api";
import { forgotPasswordSchema, type ForgotPasswordFormValues } from "../validation";

function getErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "data" in error) {
    const fetchError = error as FetchBaseQueryError;

    if (fetchError.status === "FETCH_ERROR" || fetchError.status === "PARSING_ERROR") {
      return "errors.server_unavailable";
    }

    if (typeof fetchError.status === "number" && fetchError.status >= 500) {
      return "errors.server_unavailable";
    }
  }

  return "errors.unknown";
}

export default function ForgotPasswordPage() {
  const { t } = useTranslation("ForgotPasswordPage");
  const [isSuccess, setIsSuccess] = useState(false);

  const [forgotPassword, { isLoading, error }] = useForgotPasswordMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  async function onSubmit(data: ForgotPasswordFormValues) {
    try {
      await forgotPassword({ email: data.email }).unwrap();
      setIsSuccess(true);
    } catch (err) {
      void err;
    }
  }

  if (isSuccess) {
    return (
      <AuthLayout>
        <title>{t("meta.title")}</title>
        <meta name="description" content={t("meta.description")} />
        <div className="w-full">
          <div className="max-w-sm mx-auto mt-12 flex flex-col gap-6 text-center">
            <h1 className="text-center text-display-xs content-base-primary">{t("title")}</h1>
            <p className="text-body-regular-md content-base-secondary">{t("success.message")}</p>
            <Link to={LOGIN_PAGE_ROUTE} className="text-body-regular-sm underline content-base-primary">
              {t("success.backToLogin")}
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <title>{t("meta.title")}</title>
      <meta name="description" content={t("meta.description")} />
      <div className="w-full">
        <form onSubmit={handleSubmit(onSubmit)} className="max-w-sm mx-auto mt-12 flex flex-col gap-6">
          <h1 className="text-center text-display-xs content-base-primary">{t("title")}</h1>
          <Input
            type="email"
            label={t("email.label")}
            placeholder={t("email.placeholder")}
            autoComplete="email"
            {...register("email")}
            error={errors.email?.message ? t(errors.email.message) : undefined}
          />

          {error && !isLoading && <p className="content-action-negative">{t(getErrorMessage(error))}</p>}
          <Button size="lg" variant="primary" disabled={isLoading} type="submit">
            {isLoading ? t("loading") : t("button")}
          </Button>
          <Link to={LOGIN_PAGE_ROUTE} className="text-center text-body-regular-sm underline content-base-primary">
            {t("backToLogin")}
          </Link>
        </form>
      </div>
    </AuthLayout>
  );
}
